/* ============================================================================
 * STOREFRONT.EXE — sovereign commerce layer
 * ----------------------------------------------------------------------------
 * Project merchandise and digital goods. Catalogue is data-driven; access is
 * gated by the 14-role matrix in src/lib/roles.ts.
 *
 * HONEST STATUS: there is no store contract. No cart, no checkout, no payment
 * rail is wired. What is real here is the *gating model* — you can see which
 * role a given listing requires, and whether the connected session satisfies it.
 * Purchases link out rather than fabricate an order.
 * ========================================================================= */

import { useMemo, useState } from 'react';
import {
  ROLES,
  hasPermission,
  type PermissionKey,
} from '../lib/roles';
import { useWallet } from '../hooks/useWallet';

type Kind = 'physical' | 'digital' | 'access';

interface Listing {
  sku: string;
  title: string;
  kind: Kind;
  price: string;
  /** Permission required to buy. */
  requires: PermissionKey;
  blurb: string;
  /** Physical goods need a fulfilment address we do not collect yet. */
  fulfilment?: 'shipping' | 'license' | 'token-gate';
}

const LISTINGS: readonly Listing[] = [
  {
    sku: 'MERCH-001',
    title: 'Terminal Dossier Tee',
    kind: 'physical',
    price: '0.018 ETH',
    requires: 'STORE_BUY',
    blurb: 'Navy ground, brass wordmark. Screen-printed, no plastisol.',
    fulfilment: 'shipping',
  },
  {
    sku: 'MERCH-002',
    title: 'HUD Bracket Cap',
    kind: 'physical',
    price: '0.014 ETH',
    requires: 'STORE_BUY',
    blurb: 'Six-panel, embroidered corner brackets.',
    fulfilment: 'shipping',
  },
  {
    sku: 'DIGI-001',
    title: 'Immersive Font License — Phosphate',
    kind: 'digital',
    price: '0.05 ETH',
    requires: 'STORE_BUY',
    blurb: 'Desktop + web licence for the display face. Delivered as an attestation.',
    fulfilment: 'license',
  },
  {
    sku: 'DIGI-002',
    title: 'Presence Capsule Archive',
    kind: 'digital',
    price: 'free',
    requires: 'STORE_BUY',
    blurb: 'Every moment you have attested, rendered as a browsable capsule.',
    fulfilment: 'token-gate',
  },
  {
    sku: 'ACCESS-001',
    title: 'Bubble Operator Seat',
    kind: 'access',
    price: 'requires Community',
    requires: 'STORE_SELL',
    blurb: 'Operate a bubble: invite, moderate, run events, gate guilds.',
    fulfilment: 'token-gate',
  },
];

const KIND_TAG: Record<Kind, string> = {
  physical: 'tag--muted',
  digital: 'tag--ok',
  access: 'tag--warn',
};

export function StoreFront() {
  const [sel, setSel] = useState<string | null>(null);
  const w = useWallet();
  const [roles, setRoles] = useState<string[]>([]);

  const active = useMemo(
    () => LISTINGS.find((l) => l.sku === sel) ?? null,
    [sel],
  );

  const canBuy = (l: Listing) =>
    roles.length > 0 && hasPermission(roles, l.requires);

  return (
    <div className="stack">
      <span className="eyebrow">catalogue — {LISTINGS.length} listings</span>

      <div className="panel">
        <div className="row">
          <span className="row__k">Payment rail</span>
          <span className="row__v">
            <span className="tag tag--warn">NOT WIRED</span>
          </span>
        </div>
        <div className="row">
          <span className="row__k">Fulfilment</span>
          <span className="row__v">
            <span className="tag tag--warn">NOT WIRED</span>
          </span>
        </div>
      </div>
      <p className="note note--warn">
        There is no store contract and no checkout. The catalogue below is real
        data with real role gating; buying links out. Wiring a payment rail and a
        shipping collector is outstanding work, not a hidden feature.
      </p>

      <hr className="rule" />

      {/* session roles — the gate */}
      <span className="eyebrow">session roles</span>
      <div className="hstack">
        {ROLES.slice(0, 6).map((r) => (
          <button
            key={r.id}
            type="button"
            className="btn"
            style={
              roles.includes(r.id)
                ? { borderColor: 'var(--hud-yellow)', color: 'var(--hud-yellow)' }
                : undefined
            }
            onClick={() =>
              setRoles((prev) =>
                prev.includes(r.id)
                  ? prev.filter((x) => x !== r.id)
                  : [...prev, r.id],
              )
            }
          >
            {r.label}
          </button>
        ))}
      </div>
      <p className="note">
        Toggle a role to see the gate work. Enforced locally only —{' '}
        {w.isConnected ? 'wallet connected' : 'no wallet session'}.
      </p>

      <hr className="rule" />

      <table className="grid">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Listing</th>
            <th>Price</th>
            <th>Gate</th>
          </tr>
        </thead>
        <tbody>
          {LISTINGS.map((l) => (
            <tr
              key={l.sku}
              onClick={() => setSel(l.sku)}
              style={{ cursor: 'pointer' }}
            >
              <td>{l.sku}</td>
              <td>
                <div>{l.title}</div>
                <div className="note">{l.blurb}</div>
              </td>
              <td>{l.price}</td>
              <td>
                <span className={`tag ${KIND_TAG[l.kind]}`}>{l.kind}</span>
                <div className="note">
                  needs {l.requires}
                  {roles.length > 0 && (
                    <>
                      {' · '}
                      {canBuy(l) ? (
                        <span className="tag tag--ok">PASS</span>
                      ) : (
                        <span className="tag tag--err">BLOCKED</span>
                      )}
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {active && (
        <div className="panel stack" style={{ gap: 4 }}>
          <span className="eyebrow">selected — {active.sku}</span>
          <span>{active.title}</span>
          <div className="row">
            <span className="row__k">Kind</span>
            <span className="row__v">{active.kind}</span>
          </div>
          <div className="row">
            <span className="row__k">Fulfilment</span>
            <span className="row__v">
              {active.fulfilment ?? 'none'}{' '}
              <span className="tag tag--warn">unsupported</span>
            </span>
          </div>
          <div className="row">
            <span className="row__k">Requires</span>
            <span className="row__v">{active.requires}</span>
          </div>
          <p className="note">
            {canBuy(active)
              ? 'Session satisfies the gate. No rail exists to complete an order.'
              : 'Session does not satisfy the gate.'}
          </p>
        </div>
      )}
    </div>
  );
}
